import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Building2, 
  Layers, 
  Wrench, 
  LayoutGrid, 
  Ruler, 
  Phone, 
  CheckCircle2, 
  ArrowRight,
  ClipboardList,
  Palette,
  FileCheck,
  HardHat
} from "lucide-react";

const IMAGES = {
  hero: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1200&q=80",
  about: "https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=800&q=80",
  cta: "https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=1200&q=80"
};

const services = [
  {
    icon: Building2,
    title: "Trockenbau",
    description: "Professionelle Trockenbauarbeiten für Wand- und Deckenkonstruktionen mit höchster Präzision.",
    color: "bg-amber-500/10 text-amber-400"
  },
  {
    icon: Layers,
    title: "Bodenbeläge",
    description: "Fachgerechte Verlegung von Laminat, Vinyl, Parkett und weiteren Bodenbelägen.",
    color: "bg-amber-500/10 text-amber-400"
  },
  {
    icon: Wrench,
    title: "Montagearbeiten",
    description: "Montage von Türen, Fenstern und sonstigen Bauelementen nach Maß.",
    color: "bg-amber-500/10 text-amber-400"
  },
  {
    icon: LayoutGrid,
    title: "Deckensysteme",
    description: "Installation moderner Deckensysteme inkl. abgehängter Decken und Akustiklösungen.",
    color: "bg-amber-500/10 text-amber-400"
  },
  {
    icon: Ruler,
    title: "Trockenstrich",
    description: "Schnelle und saubere Trockenstrich-Lösungen für optimale Bodenvorbereitung.",
    color: "bg-amber-500/10 text-amber-400"
  }
];

const processSteps = [
  {
    icon: ClipboardList,
    number: "01",
    title: "Analyse & Beratung",
    description: "Gemeinsam analysieren wir Ihre Anforderungen und entwickeln die beste Lösung."
  },
  {
    icon: Palette,
    number: "02",
    title: "Planung",
    description: "Detaillierte Planung mit Materialauswahl und Kostenübersicht."
  },
  {
    icon: FileCheck,
    number: "03",
    title: "Angebot",
    description: "Transparentes Angebot ohne versteckte Kosten."
  },
  {
    icon: HardHat,
    number: "04",
    title: "Umsetzung",
    description: "Professionelle Ausführung mit termingerechter Fertigstellung."
  }
];

const stats = [
  { value: "15+", label: "Jahre Erfahrung" },
  { value: "500+", label: "Projekte" },
  { value: "100%", label: "Kundenzufriedenheit" }
];

const HomePage = () => {
  return (
    <div data-testid="home-page" style={{ backgroundColor: '#372d24' }}>
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center" style={{ backgroundColor: '#372d24' }} data-testid="hero-section">
        <div className="absolute inset-0" style={{ backgroundColor: '#372d24' }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left Content */}
            <div className="space-y-8 animate-fadeInUp">
              <div className="inline-flex items-center gap-2 bg-amber-500/10 text-amber-400 px-4 py-2 rounded-full text-sm font-medium border border-amber-500/20">
                <CheckCircle2 className="h-4 w-4" />
                Ihr Partner für Innenausbau
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight" style={{ fontFamily: 'Manrope, sans-serif', color: '#dbcebe' }}>
                Professioneller<br />
                <span className="text-amber-400">Trockenbau</span><br />
                in Eberswalde
              </h1>
              <p className="text-lg max-w-xl leading-relaxed" style={{ color: '#dbcebe' }}>
                Wir setzen auf höchste Qualität und Präzision bei jedem Projekt für private als auch gewerbliche Kunden. Schöner Wohnen - Besser Leben.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/leistungen">
                  <Button 
                    size="lg" 
                    className="bg-amber-500 hover:bg-amber-600 text-black font-semibold px-8 py-6 text-base btn-shine"
                    data-testid="hero-leistungen-btn"
                  >
                    Unsere Leistungen
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/kontakt">
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="border-zinc-700 text-white hover:bg-zinc-800 px-8 py-6 text-base"
                    data-testid="hero-kontakt-btn"
                  >
                    Kostenloses Angebot
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right Image */}
            <div className="relative animate-fadeInUp animation-delay-200">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl image-zoom border border-zinc-800">
                <img 
                  src={IMAGES.hero} 
                  alt="Professioneller Trockenbau"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              </div>
              {/* Floating Card */}
              <div className="absolute -bottom-6 -left-6 p-6 rounded-xl shadow-xl animate-fadeInUp animation-delay-300 border border-[#4a3d32]" style={{ backgroundColor: '#2a2219' }}>
                <div className="flex items-center gap-4">
                  <div className="bg-amber-500 p-3 rounded-lg">
                    <Phone className="h-6 w-6 text-black" />
                  </div>
                  <div>
                    <p className="text-sm text-zinc-500">Rufen Sie uns an</p>
                    <a href="tel:+491621608568" className="font-semibold text-white hover:text-amber-400 transition-colors" data-testid="hero-phone">
                      +49 162 1608568
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-12" style={{ backgroundColor: '#372d24' }} data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <p className="text-amber-400 font-medium mb-4">Unsere Leistungen</p>
            <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ fontFamily: 'Manrope, sans-serif', color: '#dbcebe' }}>
              Alles aus einer Hand
            </h2>
            <p className="text-lg" style={{ color: '#dbcebe' }}>
              Von Trockenbau bis Bodenbeläge – wir bieten Ihnen ein umfassendes Leistungsspektrum für Ihren Innenausbau.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Card 
                key={service.title}
                className="service-card border border-[#4a3d32] hover:border-amber-500/50 cursor-pointer group"
                style={{ backgroundColor: '#372d24' }}
                data-testid={`service-card-${index}`}
              >
                <CardContent className="p-8">
                  <div className={`inline-flex p-4 rounded-xl ${service.color} mb-6 group-hover:scale-110 transition-transform`}>
                    <service.icon className="h-7 w-7" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Manrope, sans-serif', color: '#dbcebe' }}>
                    {service.title}
                  </h3>
                  <p className="leading-relaxed" style={{ color: '#a89f94' }}>
                    {service.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/leistungen">
              <Button 
                variant="outline" 
                className="border-zinc-700 text-white hover:bg-zinc-800 hover:text-amber-400"
                data-testid="services-more-btn"
              >
                Alle Leistungen ansehen
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-12" style={{ backgroundColor: '#372d24' }} data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-xl image-zoom border border-zinc-800">
                <img 
                  src={IMAGES.about} 
                  alt="Unser Team bei der Arbeit"
                  className="w-full h-[450px] object-cover"
                />
              </div>
              <div className="absolute -bottom-8 -right-8 bg-amber-500 text-black p-8 rounded-2xl shadow-xl hidden md:block">
                <p className="text-4xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>15+</p>
                <p className="text-amber-900 font-medium">Jahre Erfahrung</p>
              </div>
            </div>

            <div className="space-y-8">
              <div>
                <p className="text-amber-400 font-medium mb-4">Über uns</p>
                <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ fontFamily: 'Manrope, sans-serif', color: '#dbcebe' }}>
                  Qualität und Präzision auf höchstem Niveau
                </h2>
                <p className="text-lg leading-relaxed mb-6" style={{ color: '#dbcebe' }}>
                  Als erfahrener Partner für Trockenbau, Bodenbeläge und Innenausbau in Eberswalde bieten wir maßgeschneiderte Lösungen für private und gewerbliche Projekte.
                </p>
                <p className="leading-relaxed" style={{ color: '#a89f94' }}>
                  Von der Planung über die Materialauswahl bis zur fachgerechten Umsetzung – unser Anspruch ist es, Ihnen Räume zu schaffen, die sowohl funktional als auch ästhetisch überzeugen.
                </p>
              </div>

              <ul className="space-y-4">
                <li className="flex items-center gap-3">
                  <CheckCircle2 className="h-6 w-6 text-amber-400 flex-shrink-0" />
                  <span style={{ color: '#dbcebe' }}>Höchste Qualität und präzise Ausführung</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle2 className="h-6 w-6 text-amber-400 flex-shrink-0" />
                  <span style={{ color: '#dbcebe' }}>Zuverlässigkeit und Termintreue</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle2 className="h-6 w-6 text-amber-400 flex-shrink-0" />
                  <span style={{ color: '#dbcebe' }}>Faire und transparente Preise</span>
                </li>
              </ul>

              <Link to="/kontakt">
                <Button 
                  size="lg" 
                  className="bg-amber-500 hover:bg-amber-600 text-black font-semibold px-8"
                  data-testid="about-kontakt-btn"
                >
                  Kontakt aufnehmen
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-12" style={{ backgroundColor: '#372d24' }} data-testid="process-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <p className="text-amber-400 font-medium mb-4">Unser Prozess</p>
            <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ fontFamily: 'Manrope, sans-serif', color: '#dbcebe' }}>
              In 4 Schritten zu Ihrem Projekt
            </h2>
            <p className="text-lg" style={{ color: '#dbcebe' }}>
              Wir begleiten Sie durch einen strukturierten Prozess für ein perfektes Ergebnis.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <div 
                key={step.number}
                className="relative text-center p-6 rounded-2xl border border-[#4a3d32]"
                style={{ backgroundColor: '#372d24' }}
                data-testid={`process-step-${index}`}
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-500/10 rounded-2xl mb-6">
                  <step.icon className="h-8 w-8 text-amber-400" />
                </div>
                <div className="absolute top-4 right-4 text-5xl font-bold text-zinc-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {step.number}
                </div>
                <h3 className="text-lg font-semibold mb-3" style={{ fontFamily: 'Manrope, sans-serif', color: '#dbcebe' }}>
                  {step.title}
                </h3>
                <p className="text-sm" style={{ color: '#a89f94' }}>
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-10 bg-amber-500" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <div 
                key={stat.label}
                className="text-center"
                data-testid={`stat-${index}`}
              >
                <p className="text-4xl md:text-5xl font-bold text-black mb-2 stat-number" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {stat.value}
                </p>
                <p className="text-amber-900 text-sm md:text-base font-medium">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 relative overflow-hidden" style={{ backgroundColor: '#372d24' }} data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="rounded-3xl p-12 md:p-16 text-center relative overflow-hidden border border-[#4a3d32]" style={{ backgroundColor: '#2a2219' }}>
            <div className="absolute inset-0 opacity-5">
              <img 
                src={IMAGES.cta} 
                alt="" 
                className="w-full h-full object-cover"
              />
            </div>
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ fontFamily: 'Manrope, sans-serif', color: '#dbcebe' }}>
                Starten Sie Ihr Projekt mit uns!
              </h2>
              <p className="text-lg mb-8 max-w-2xl mx-auto" style={{ color: '#a89f94' }}>
                Kontaktieren Sie uns für eine unverbindliche Beratung. Wir freuen uns darauf, Ihre Ideen zu verwirklichen.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/kontakt">
                  <Button 
                    size="lg" 
                    className="bg-amber-500 hover:bg-amber-600 text-black font-semibold px-8 py-6 text-base"
                    data-testid="cta-termin-btn"
                  >
                    Termin vereinbaren
                  </Button>
                </Link>
                <a href="tel:+491621608568">
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="border-zinc-700 text-white hover:bg-zinc-800 px-8 py-6 text-base"
                    data-testid="cta-anrufen-btn"
                  >
                    <Phone className="mr-2 h-5 w-5" />
                    Jetzt anrufen
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
